import { MessageSquareText, UserRound } from "lucide-react";
import type {
  Athlete,
  LinkedAthlete,
  LinkedAthleteAssignment,
  MePayload,
} from "@/lib/types";
import { Badge } from "@/components/ui/Badge";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableHeaderRow,
  DataTableRow,
  DataTableScroll,
} from "@/components/ui/DataTable";
import { useMeAthletes } from "@/features/athletes/hooks/useAthletes";
import { usePlayerPhoto } from "@/features/player-photos/usePlayerPhoto";

/**
 * #908 — Account → Profile "Linked athletes" (plan §7). One card per athlete
 * the signed-in account holds a current link to (self / guardian / family)
 * with that athlete's assignment history. `GET /api/me/athletes` already
 * limits the rows to assignments where the account still holds the
 * projected membership (D5), so this panel renders exactly what it gets and
 * nothing else: never other athletes, feedback counts or team staff.
 * Club-host only — the apex and operator hosts resolve no club, and the
 * hook stays disabled there.
 */

const RELATIONSHIP_LABELS: Record<LinkedAthlete["relationship"], string> = {
  self: "You",
  guardian: "Guardian",
  family: "Family",
};

/** Athlete-first Feedback history (P3d): `/feedback?athlete=<id>` on the
 * club host. A plain path so the link does not depend on the Feedback
 * route's search schema. */
export function linkedAthleteFeedbackHref(athleteId: string): string {
  return `/feedback?athlete=${encodeURIComponent(athleteId)}`;
}

/** Newest assignment first: by start date, then season name. */
export function sortLinkedAssignments(
  assignments: LinkedAthleteAssignment[],
): LinkedAthleteAssignment[] {
  return [...assignments].sort((a, b) => {
    const byStart = (b.starts_on ?? "").localeCompare(a.starts_on ?? "");
    if (byStart !== 0) return byStart;
    return (b.season_name ?? "").localeCompare(a.season_name ?? "");
  });
}

function assignmentDates(assignment: LinkedAthleteAssignment): string {
  if (!assignment.starts_on && !assignment.ends_on) return "—";
  return `${assignment.starts_on ?? "—"} → ${assignment.ends_on ?? "present"}`;
}

function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "--"
  );
}

/** The athlete's photo is readable through any of its assignments'
 * `player_id` (D15); without one there is nothing to fetch. */
function LinkedAthleteAvatar({
  athlete,
  playerId,
}: {
  athlete: Athlete;
  playerId: string | null;
}) {
  const url = usePlayerPhoto("player", playerId, athlete.photo_filename);
  return (
    <span className="profile-team-avatar overflow-hidden" aria-hidden="true">
      {url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
      ) : (
        initialsFor(athlete.display_name)
      )}
    </span>
  );
}

function LinkedAthleteCard({ linked }: { linked: LinkedAthlete }) {
  const { athlete, relationship } = linked;
  const assignments = sortLinkedAssignments(linked.assignments ?? []);
  return (
    <article
      data-testid="me-linked-athlete"
      data-athlete-id={athlete.id}
      className="min-w-0 overflow-hidden rounded-inset border border-rule bg-elevated p-3"
    >
      <div className="flex items-center gap-3">
        <LinkedAthleteAvatar
          athlete={athlete}
          playerId={assignments[0]?.player_id ?? null}
        />
        <div className="min-w-0 flex-1">
          <p
            data-testid="me-linked-athlete-name"
            className="truncate text-sm font-semibold text-ink"
          >
            {athlete.display_name}
          </p>
          <Badge
            variant={relationship === "self" ? "accent" : "outline"}
            data-testid="me-linked-athlete-relationship"
            className="mt-1"
          >
            {RELATIONSHIP_LABELS[relationship]}
          </Badge>
        </div>
      </div>

      {assignments.length > 0 ? (
        <DataTableScroll className="mt-3 rounded-inset border border-rule bg-surface">
          <DataTable>
            <DataTableHead>
              <DataTableHeaderRow>
                <DataTableHeadCell>Season</DataTableHeadCell>
                <DataTableHeadCell>Team</DataTableHeadCell>
                <DataTableHeadCell>Jersey</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell>Dates</DataTableHeadCell>
              </DataTableHeaderRow>
            </DataTableHead>
            <DataTableBody>
              {assignments.map((assignment) => {
                const archived = assignment.team_status === "archived";
                return (
                  <DataTableRow
                    key={assignment.player_id}
                    interactive={false}
                    data-testid="me-linked-athlete-assignment"
                    data-player-id={assignment.player_id}
                    data-team-id={assignment.team_id}
                    data-archived={archived ? "true" : undefined}
                    className={archived ? "text-ink-muted" : "text-ink"}
                  >
                    <DataTableCell>
                      {assignment.season_name ?? "Unassigned (legacy)"}
                    </DataTableCell>
                    <DataTableCell>
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {assignment.team_name}
                        {archived ? (
                          <Badge variant="muted">Archived</Badge>
                        ) : null}
                      </span>
                    </DataTableCell>
                    <DataTableCell className="font-mono">
                      {assignment.jersey_number || "—"}
                    </DataTableCell>
                    <DataTableCell>
                      {assignment.assignment_status === "ended" ? (
                        <Badge variant="muted">Ended</Badge>
                      ) : (
                        <Badge variant="good">Active</Badge>
                      )}
                    </DataTableCell>
                    <DataTableCell className="whitespace-nowrap font-mono text-ink-muted">
                      {assignmentDates(assignment)}
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        </DataTableScroll>
      ) : (
        <p className="mt-3 text-xs text-ink-muted">
          No team assignments on record.
        </p>
      )}

      <a
        href={linkedAthleteFeedbackHref(athlete.id)}
        data-testid="me-linked-athlete-feedback-link"
        className="mt-3 inline-flex items-center gap-1.5 font-display text-xs uppercase tracking-wider text-accent hover:underline"
      >
        <MessageSquareText size={14} strokeWidth={2.2} />
        View feedback history
      </a>
    </article>
  );
}

export function LinkedAthletesPanel({ me }: { me: MePayload }) {
  const onClubHost = Boolean(me.current_club);
  const athletes = useMeAthletes({ enabled: onClubHost });
  if (!onClubHost) return null;

  return (
    <section data-testid="me-linked-athletes" className="profile-team-panel">
      <h3>Linked athletes</h3>
      {athletes.isPending ? (
        <div
          data-testid="me-linked-athletes-loading"
          aria-busy="true"
          className="mt-5 grid gap-2"
        >
          <span className="block h-10 animate-pulse rounded-inset bg-elevated" />
          <span className="block h-24 animate-pulse rounded-inset bg-elevated" />
        </div>
      ) : athletes.isError ? (
        <p className="mt-3 rounded-inset border border-rule bg-elevated px-3 py-2 text-xs text-destructive">
          Couldn't load your linked athletes. Try again later.
        </p>
      ) : athletes.data && athletes.data.length > 0 ? (
        <div className="mt-5 grid gap-3">
          {athletes.data.map((linked) => (
            <LinkedAthleteCard key={linked.athlete.id} linked={linked} />
          ))}
        </div>
      ) : (
        <div
          data-testid="me-linked-athletes-empty"
          className="mt-5 flex items-start gap-3 rounded-inset border border-rule bg-elevated p-3"
        >
          <span className="profile-team-avatar" aria-hidden="true">
            <UserRound size={18} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <p className="text-sm text-ink">
              No athletes are linked to this account yet.
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              A coach or club admin links an account to an athlete from the
              team roster.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
